import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, Sparkles } from "lucide-react";
import { useInterests, useUserInterests, Interest } from "@/hooks/useInterests";
import { toast } from "@/hooks/use-toast";

interface InterestSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InterestSelectorModal = ({ isOpen, onClose }: InterestSelectorModalProps) => {
  const { interestsByCategory, loading: loadingAll } = useInterests();
  const { userInterestIds, toggleInterest, loading: loadingUser } = useUserInterests();
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const loading = loadingAll || loadingUser;

  const handleToggle = async (interest: Interest) => {
    setPendingIds((prev) => new Set(prev).add(interest.id));
    
    const { error } = await toggleInterest(interest.id);
    
    setPendingIds((prev) => {
      const next = new Set(prev);
      next.delete(interest.id);
      return next;
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const categories = Object.keys(interestsByCategory).sort();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Your Interests
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Select interests to find compatible matches. The more you add, the better your matches!
        </p>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          ) : (
            categories.map((category) => (
              <div key={category}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {interestsByCategory[category].map((interest) => {
                    const isSelected = userInterestIds.has(interest.id);
                    const isPending = pendingIds.has(interest.id);

                    return (
                      <button
                        key={interest.id}
                        onClick={() => handleToggle(interest)}
                        disabled={isPending}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted hover:bg-muted/80 text-foreground"
                        } ${isPending ? "opacity-50" : ""}`}
                      >
                        {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                        {interest.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">
              {userInterestIds.size} interests selected
            </span>
            {userInterestIds.size >= 5 && (
              <span className="text-xs text-primary font-medium">
                Great for matching! ✨
              </span>
            )}
          </div>
          <Button onClick={onClose} className="w-full pop-gradient">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InterestSelectorModal;
